/**
 * Synchro de nuit du catalogue : chaque nuit a 3 h, heure de Toronto, si
 * l'app est ouverte (plan, PR 1.4). L'heure de Toronto suit l'heure
 * avancee : 3 h existe tous les jours (le changement d'heure se fait a 2 h).
 *
 * nextNightlyRun est pur (teste avec des dates fixes) ; useNightly verifie
 * chaque minute si l'heure est passee, ce qui rattrape aussi une nuit ou le
 * poste etait en veille a 3 h (synchro au reveil).
 */
import { useEffect, useRef } from 'react';

export const NIGHTLY_HOUR = 3;
export const NIGHTLY_TIME_ZONE = 'America/Toronto';
export const NIGHTLY_CHECK_MS = 60_000;

const formatters = new Map();
function formatterFor(timeZone) {
    if (!formatters.has(timeZone)) {
        formatters.set(timeZone, new Intl.DateTimeFormat('en-CA', {
            timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        }));
    }
    return formatters.get(timeZone);
}

/** Date et heure murales dans `timeZone` a l'instant `ms`. */
function wallClock(ms, timeZone) {
    const parts = {};
    for (const { type, value } of formatterFor(timeZone).formatToParts(new Date(ms))) parts[type] = Number(value);
    return parts;
}

/** Ecart (ms) entre l'heure murale de `timeZone` et UTC a l'instant `ms`. */
function zoneOffset(ms, timeZone) {
    const p = wallClock(ms, timeZone);
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    return asUtc - (ms - (((ms % 1000) + 1000) % 1000));
}

/** Instant UTC de l'heure murale y-m-d h:00 dans `timeZone`. */
function zonedToUtc(year, month, day, hour, timeZone) {
    const guess = Date.UTC(year, month - 1, day, hour);
    const first = guess - zoneOffset(guess, timeZone);
    const second = guess - zoneOffset(first, timeZone);
    return second;
}

/**
 * Prochain passage a `hour` h (heure de `timeZone`) strictement apres `nowMs`.
 * @returns {number} millisecondes epoch
 */
export function nextNightlyRun(nowMs, { hour = NIGHTLY_HOUR, timeZone = NIGHTLY_TIME_ZONE } = {}) {
    const today = wallClock(nowMs, timeZone);
    const candidate = zonedToUtc(today.year, today.month, today.day, hour, timeZone);
    if (candidate > nowMs) return candidate;
    const tomorrow = new Date(Date.UTC(today.year, today.month - 1, today.day + 1));
    return zonedToUtc(tomorrow.getUTCFullYear(), tomorrow.getUTCMonth() + 1, tomorrow.getUTCDate(), hour, timeZone);
}

/**
 * Appelle `onRun` a chaque passage de 3 h (heure de Toronto) tant que
 * `enabled` est vrai.
 */
export function useNightly({ enabled, onRun, checkMs = NIGHTLY_CHECK_MS, now = () => Date.now() }) {
    const onRunRef = useRef(onRun);
    onRunRef.current = onRun;
    useEffect(() => {
        if (!enabled) return undefined;
        let next = nextNightlyRun(now());
        const id = setInterval(() => {
            const current = now();
            if (current >= next) {
                next = nextNightlyRun(current);
                onRunRef.current();
            }
        }, checkMs);
        return () => clearInterval(id);
    }, [enabled, checkMs, now]);
}
